/**
 * Mini cart component
 * Displays cart items, quantities, pricing, discounts, promolines, and free shipping progress
 */

import { useEffect, useLayoutEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, formatMoneyWithCurrency, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { useGwp } from '@/components/hooks/useGwp'
import { GwpCartBlock, GwpGiftItem } from '@/components/custom-elements/gwp-cart-block'

const actionKeys = {
  SELECT: 'select',
  REMOVE: 'remove'
}

const QUANTITY_OPTIONS_MAX = 10

const getLineItemDisplayName = (item) =>
  item.customer_facing_product_name || item.product_title

/**
 * Resolves the max quantity a line can be increased to.
 * Returns null (no limit) when inventory is untracked or the variant is set to
 * continue selling when out of stock. Otherwise caps at the tracked stock level.
 */
const getInventoryLimit = (item) => {
  const isTracked = Boolean(item.inventory_management)
  const sellsWhenOutOfStock = item.inventory_policy === 'continue'
  if (!isTracked || sellsWhenOutOfStock) return null
  return typeof item.inventory_quantity === 'number' ? item.inventory_quantity : null
}

const buildQuantityOptions = (currentQuantity, inventoryLimit) => {
  let max = QUANTITY_OPTIONS_MAX
  if (typeof inventoryLimit === 'number' && inventoryLimit > 0 && inventoryLimit < max) {
    max = inventoryLimit
  }
  if (currentQuantity > max) {
    max = currentQuantity
  }
  if (max < 1) max = 1
  return Array.from({ length: max }, (_, i) => i + 1)
}

const onChangeActionValue = (key, target) => {
  return {
    [actionKeys.SELECT]: parseInt(target.value, 10),
    [actionKeys.REMOVE]: 0
  }[key]
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig, itemLoading, setItemLoading }) => {
  const [error, setError] = useState(null)
  const [optimisticQuantity, setOptimisticQuantity] = useState(null)

  const quantityOptions = useMemo(
    () => buildQuantityOptions(item.quantity, getInventoryLimit(item)),
    [item.quantity, item.inventory_quantity, item.inventory_management, item.inventory_policy]
  )

  useEffect(() => {
    setOptimisticQuantity(null)
  }, [item.quantity])

  const handleLineItemQuantityChange = (item, newQuantity) => {
    const inventoryLimit = getInventoryLimit(item)
    if (newQuantity > 0 && inventoryLimit !== null && newQuantity > inventoryLimit) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const applyQuantityChange = async (newQuantity) => {
    if (itemLoading) return
    try {
      handleLineItemQuantityChange(item, newQuantity)
    } catch (err) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: err.message
      })
      return
    }
    if (newQuantity > 0) setOptimisticQuantity(newQuantity)
    setItemLoading(true)
    try {
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
    } catch (error) {
      setOptimisticQuantity(null)
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
    } finally {
      setItemLoading(false)
    }
  }

  const onLineItemQuantityChange = async (e) => {
    e.preventDefault()
    const action = e.target.dataset.action
    const newQuantity = onChangeActionValue(action, e.target)
    await applyQuantityChange(newQuantity)
  }

  const onRemoveClick = async (e) => {
    e.preventDefault()
    await applyQuantityChange(0)
  }

  return (
    <div className="grow flex items-end justify-between gap-x-0.5">
      <form onChange={onLineItemQuantityChange} className="shrink-0 flex flex-col">
        <label className="relative flex items-center w-fit h-[44px] py-0.25 cursor-pointer link-fade">
          <span className="absolute left-0 top-1/2 -translate-y-1/2 paragraph-xs whitespace-nowrap">Qty: </span>
          <select
            data-action={actionKeys.SELECT}
            value={optimisticQuantity ?? item.quantity}
            className="paragraph-xs leading-none h-full pl-1.625 pr-0.75 py-0 border-0 bg-none bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 z-2"
            aria-label="Quantity"
          >
            {quantityOptions.map((qty) => (
              <option value={qty} key={qty}>{qty}</option>
            ))}
          </select>
          <span className="absolute right-0 top-1/2 -translate-y-1/2 w-[8px] h-[4px] pointer-events-none children:size-full">
            <Icon id="chevron-down" />
          </span>
        </label>
        <p
          className="empty:hidden w-fit max-h-0 pt-0.5 opacity-0 paragraph-xs text-type-error transition-all"
          data-quantity-error
        >
          {error}
        </p>
      </form>

      <div className="mb-0.25">
        <a
          href={item.url_to_remove || '#'}
          className="btn btn-text btn-tertiary btn-s w-fit"
          aria-label="Remove item"
          onClick={onRemoveClick}
        >
          <span className="text-line text-line-large">
            Remove
          </span>
        </a>
      </div>
    </div>
  )
}

/**
 *
 *
 * Mini Cart free shipping bar.
 *
 * Driven by the active market's shipping settings metaobjects
 * (market.metafields.custom.shipping_settings), exposed on
 * window.globals.metaobjects.shipping_settings and ordered by min_threshold.
 * Renders nothing when the market has no shipping settings assigned.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal }) {
  const shippingSettings = window.globals?.metaobjects?.shipping_settings ?? []
  if (!shippingSettings.length) return null

  const sortedSettings = [...shippingSettings].sort(
    (a, b) => Number(a.min_threshold) - Number(b.min_threshold)
  )
  const firstSetting = sortedSettings[0]
  // Highest threshold the cart has reached, or null when below the first.
  const achievedSetting = sortedSettings.reduce(
    (reached, setting) => (cartSubTotal >= Number(setting.min_threshold) ? setting : reached),
    null
  )

  const message = achievedSetting
    ? `You are receiving ${achievedSetting.title} on this order`
    : `Spend ${formatMoney(Number(firstSetting.min_threshold) - cartSubTotal)} more to unlock ${firstSetting.title}`

  const progressBarPercentage = achievedSetting
    ? 100
    : Math.round((cartSubTotal / Number(firstSetting.min_threshold)) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" className="flex flex-col border-b border-borders-default">
      <div className="relative w-full h-[4px] bg-page-tertiary">
        <div
          className="absolute inset-tl w-shipping-bar-width h-full bg-brand-black transition-all"
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
      <div className="flex px-1 md:px-1.5 py-0.875">
        <p className="paragraph-xs">{message}</p>
      </div>
    </div>
  )
}

/** Flattens a Shopify rich-text metafield value (or plain string) to text. */
const extractTextContent = (node) => {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractTextContent).join('')
  if (typeof node === 'object') {
    if (typeof node.value === 'string') return node.value
    if (Array.isArray(node.children)) return node.children.map(extractTextContent).join('')
  }
  return ''
}

/**
 *
 *
 * Mini Cart Spend & Save bar.
 *
 * Mirrors snippets/cart/spend-and-save.liquid: a single fill bar on top with
 * every tier shown as a segmented cell beneath it (GWP progress bar styling),
 * rather than only showing the next tier's message.
 *
 * Tiers are sorted ascending by min_threshold and country filtered. Each cell
 * shows the tier's success_message once met, otherwise its message with
 * `[money]` replaced by the (bold) amount still required to reach that tier.
 * The fill bar splits evenly per tier and fills proportionally toward each
 * threshold (mirrors GwpProgressBar's segmented percentage).
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSpendAndSave({ cartTotal, tiers = [] }) {
  const currentCountry = globalState.localization?.country
  const filtered = currentCountry
    ? tiers.filter(
      (tier) =>
        !tier.available_countries?.length ||
          tier.available_countries.some((c) => c.country_code === currentCountry)
    )
    : tiers
  const sorted = [...filtered].sort((a, b) => a.min_threshold - b.min_threshold)

  if (!sorted.length) return null

  const total = cartTotal || 0

  let completedCount = 0
  let prevThreshold = 0
  let pendingCaptured = false
  let pendingNumer = 0
  let pendingDenom = 0

  const cells = sorted.map((tier, index) => {
    const qualified = total >= tier.min_threshold
    if (qualified) {
      completedCount += 1
      prevThreshold = tier.min_threshold
    } else if (!pendingCaptured) {
      pendingCaptured = true
      pendingDenom = tier.min_threshold - prevThreshold
      pendingNumer = Math.max(0, total - prevThreshold)
    }

    const rawMessage = qualified ? tier.success_message : tier.message
    const money = `<span class="font-bold">${formatMoney(tier.min_threshold - total)}</span>`
    const messageHtml = extractTextContent(rawMessage).replace(/\[money\]/g, money)

    const cellColour = qualified ? tier.success_text_colour : tier.text_colour
    const textClass = cellColour
      ? 'text-[--ss-cell-colour]'
      : qualified
        ? 'text-type-black-primary'
        : 'text-type-black-secondary'
    const borderClass = index > 0 ? 'border-l border-borders-default' : ''

    return (
      <div
        key={tier.id ?? index}
        className={`grow w-0 flex flex-col items-center justify-center text-center px-1 pt-1.125 pb-0.875 -mt-0.25 z-2 ${textClass} ${borderClass}`}
        style={cellColour ? { '--ss-cell-colour': cellColour } : undefined}
      >
        <span className="paragraph-xs font-base-spaced uppercase" dangerouslySetInnerHTML={{ __html: messageHtml }} />
      </div>
    )
  })

  let percentage = (completedCount / sorted.length) * 100
  if (pendingCaptured && pendingDenom > 0) {
    percentage += ((pendingNumer / pendingDenom) / sorted.length) * 100
  }
  percentage = Math.min(100, Math.round(percentage))

  return (
    <div data-role="spend_and_save_monitor" className="border-b border-borders-default">
      <div className="relative w-full h-[4px] bg-page-tertiary">
        <div
          className="absolute inset-tl w-spend-save-bar-width h-full bg-brand-black transition-all"
          style={{ '--spend-save-bar-width': percentage + '%' }}
        ></div>
      </div>
      <div className="flex items-stretch w-full">
        {cells}
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="flex flex-col items-end text-right">
        <p className="paragraph-xs text-type-black-secondary line-through">{formatMoneyWithCurrency(originalPrice)}</p>
        <p className="paragraph-xs">{formatMoneyWithCurrency(salePrice)}</p>
      </div>
    )
  }

  // Priority order: Discounts -> Compare At -> Regular (matches cart-line-item.liquid)
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div className="flex flex-col items-end text-right">
      <p className="paragraph-xs">{formatMoneyWithCurrency(item.line_price)}</p>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  if (!item.discounts?.length) return null
  return (
    <ul className="mt-0.5 list-none">
      {item.discounts.map((discount, index) => (
        <li className="flex" key={`${discount.title}-${index}`}>
          <span className="paragraph-xs font-base-spaced uppercase text-type-black-secondary">{discount.title}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath, ariaLabel }) => {
  return (
    <a href={productPath} className="shrink-0 link-fade" aria-label={ariaLabel || 'Item image'}>
      <PreactImage key={id} imageUrl={[imageSrc]} className="flex w-[94px] md:w-[110px] aspect-2/3 children:size-full children:object-cover" alt={ariaLabel || ''} />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && typeof properties === 'object' && Object.keys(properties).length > 0

  const optionParts = (item.options_with_values ?? []).map(
    (option) => `${option.name}: ${option.value}`
  )

  const propertyEntries = hasProperties && properties
    ? Object.entries(properties).filter(([key, val]) => val != null && String(val) !== '' && key && !key.startsWith('_'))
    : []

  const title = getLineItemDisplayName(item)

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle],
    location: 'Minicart'
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div className="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div className="flex flex-col">
        <p className="paragraph-xs font-base-spaced uppercase link-fade">
          {title}
        </p>

        {optionParts.length > 0 && (
          <p className="paragraph-xs text-type-black-secondary">
            {optionParts.join(' | ')}
          </p>
        )}

        {propertyEntries.length > 0 && (
          <ul className="list-none">
            {propertyEntries.map(([key, val]) => (
              <li key={key} className="list-none">
                <span className="paragraph-xs text-type-black-secondary">{key}: {String(val)}</span>
              </li>
            ))}
          </ul>
        )}

        {(firstPromoline || secondPromoline) && (
          <div className="flex flex-col gap-0.25">
            {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />}
            {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />}
          </div>
        )}

        <MiniCartItemDiscount item={item} />
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const [itemLoading, setItemLoading] = useState(false)
  const loadingClass = itemLoading ? 'opacity-50 pointer-events-none' : ''

  const title = getLineItemDisplayName(item)

  return (
    <div className={`flex flex-col border-b border-borders-default transition-all ${loadingClass}`}>
      <div className="flex items-stretch">
        <MiniCartItemImage
          imageSrc={item.featured_image.url}
          id={item.id}
          productPath={item.url}
          ariaLabel={title}
        />
        <div className="grow flex flex-col gap-y-0.5 h-auto p-1 pb-0">
          <MiniCartItemDetails item={item} />
          {gift && (
            <GwpGiftItem
              gift={gift}
              onAddToCart={({ loading, cart: newCart }) => {
                if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
                if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
              }}
              isLoading={isLoading}
            />
          )}
          <MiniCartItemQuantity
            item={item}
            handleSetCartConfig={handleSetCartConfig}
            itemLoading={itemLoading}
            setItemLoading={setItemLoading}
          />
        </div>
      </div>
    </div>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, isLoading, totalText }) => {
  const cartTotal = formatMoneyWithCurrency(total)

  return (
    <div className="flex justify-between gap-x-1 px-margin py-1 border-b border-borders-default">
      <p className="paragraph-xs font-base-spaced font-semibold uppercase">{totalText || 'Total'}</p>
      <p className={`paragraph-xs font-base-spaced font-semibold text-right transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ emptyState }) => {
  const emptyDescription = emptyState?.description
  const emptyLink = emptyState?.link?.url
  const emptyLinkText = emptyState?.link?.text

  return (
    <>
      <div className="grow flex flex-col justify-center gap-y-1 p-margin transition-all">
        {emptyDescription && <p className={'paragraph-xs'}>{emptyDescription}</p>}
        {emptyLinkText && (
          <a href={emptyLink} className={'btn btn-primary btn-border btn-m w-full'} aria-label={emptyLinkText}>{emptyLinkText}</a>
        )}
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div className="px-margin pt-1">
      <form action={globalState.routes.cart_url} method="post">
        <button type="submit" name="checkout" className="btn btn-primary btn-fill btn-m lg:btn-l w-full" aria-label={checkoutButtonTitle}>
          {checkoutButtonTitle}
        </button>
      </form>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  return (
    <div className="grow flex flex-col" data-role="cart_items">
      {cart.items.map((item) => {
        const giftToDisplay = giftsWithoutThreshold.find(gift => {
          if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
            return false
          }
          switch (gift.conditionalType) {
            case 'all':
              return true
            case 'products':
              return gift.conditionalIds.some(id => id.split('/').pop() === item.product_id.toString())
            case 'collections':
              return item.collections?.some(collection => gift.conditionalIds.some(id => id.split('/').pop() === collection.id.toString()))
            case 'tags':
              return item.tags?.some(tag => gift.conditionalTags.includes(tag))
            default:
              return false
          }
        })
        if (giftToDisplay) {
          giftsDisplayed.push(giftToDisplay.id)
        }
        return (
          <MiniCartLineItem
            key={item.key}
            item={{ ...item }}
            gift={giftToDisplay}
            handleSetCartConfig={handleSetCartConfig}
            isLoading={isLoading}
          />
        )
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const { giftsWithoutThreshold } = useGwp(cart, freeShippingMinimum)

  // Spend & Save visibility flag lives on the minicart-summary block settings.
  const summaryBlock = document.getElementById('minicart-summary')
  const showSpendAndSave = summaryBlock?.dataset.showSpendAndSave !== 'false'

  useLayoutEffect(() => {
    document.dispatchEvent(
      new CustomEvent('minicart:cart-rendered', {
        detail: { itemCount: cart?.item_count ?? 0 }
      })
    )
  }, [cart?.item_count])

  const { checkoutButtonTitle, totalText, empty: emptyState } = globalCart

  useEffect(() => {
    const contentEl = document.getElementById('minicart-content')
    if (contentEl) {
      contentEl.classList.toggle('opacity-50', isLoading)
      contentEl.classList.toggle('pointer-events-none', isLoading)
    }
  }, [isLoading])

  const hasCartItems = cart?.items?.length > 0

  useEffect(() => {
    const summaryWrapper = document.getElementById('minicart-summary')
    const checkoutButtons = document.querySelectorAll(
      'div.additional-checkout-buttons--vertical, div.additional-checkout-buttons--horizontal'
    )

    if (summaryWrapper) {
      summaryWrapper.classList.toggle('hidden', !hasCartItems)
    }

    checkoutButtons.forEach((buttonGroup) => {
      buttonGroup.classList.toggle('hidden', !hasCartItems)
    })
  }, [hasCartItems])

  useEffect(() => {
    const handleRenderMiniCart = () => {
      if (!window.Cart) return
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }

    document.addEventListener('renderMiniCart', handleRenderMiniCart)
    return () => document.removeEventListener('renderMiniCart', handleRenderMiniCart)
  }, [])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-gwp">
            <GwpCartBlock
              cart={cart}
              freeShippingMinimum={freeShippingMinimum}
              enableProgressBar={globalCart.gwp?.enableProgressBar}
              handleSetCartConfig={handleSetCartConfig}
              isLoading={isLoading}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} giftsWithoutThreshold={giftsWithoutThreshold} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary-mount">
            <div className="flex flex-col">
              {showSpendAndSave && (
                <MiniCartSpendAndSave
                  cartTotal={cart.total_price}
                  tiers={globalState.metaobjects?.spend_and_save}
                />
              )}
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart emptyState={emptyState} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
